/**
 * ============================================================================
 * [P3-05-B] BackupTab - 백업 탭 메인 컴포넌트
 *
 * 기능:
 * - 백업 생성 (JSON 다운로드) - Firestore 데이터 수집 후 JSON 파일로 내보내기
 * - 백업 복원 (JSON 업로드) - JSON 파일에서 Firestore로 데이터 복원
 * - 백업 히스토리 표시
 *
 * [2026-01-28] P3-05 백업 기능 완전 구현
 * - texts, urlLinks, scripts 컬렉션 백업/복원 지원
 * - 기존 js/backup.js 파일 포맷과 호환
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback } from 'react'
import { useAuth } from '@/hooks'
import {
  collection,
  getDocs,
  doc,
  setDoc,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/services/firebase'
import { BackupActions } from './BackupActions'
import { BackupHistory } from './BackupHistory'
import { RestoreConfirm } from './RestoreConfirm'
import styles from './Backup.module.css'

// ============================================================================
// 1-1. 백업 데이터 타입 정의
// ============================================================================
interface BackupData {
  version: string
  exportedAt: string
  userId: string
  userEmail: string
  data: {
    texts: unknown[]
    trackingPosts: unknown[]
    urlLinks: unknown[]
    scripts?: unknown[]
  }
  counts: {
    texts: number
    trackingPosts: number
    urlLinks: number
    scripts?: number
  }
}

// ============================================================================
// 2. BackupTab Component
// ============================================================================
export function BackupTab() {
  // ========================================================================
  // 2-1. Hooks
  // ========================================================================
  const { user } = useAuth()
  // useTexts는 실시간 구독용이므로, 백업 시에는 직접 Firestore 쿼리 사용
  const [restoreFile, setRestoreFile] = useState<File | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [lastBackupDate, setLastBackupDate] = useState<Date | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusType, setStatusType] = useState<'success' | 'error' | null>(null)

  // ========================================================================
  // 2-2. Firestore Timestamp를 직렬화 가능한 형식으로 변환
  // ========================================================================
  const serializeData = useCallback((data: Record<string, unknown>): Record<string, unknown> => {
    const serialized: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) {
        serialized[key] = null
      } else if (value instanceof Timestamp) {
        // Firestore Timestamp를 ISO 문자열로 변환
        serialized[key] = value.toDate().toISOString()
      } else if (value instanceof Date) {
        serialized[key] = value.toISOString()
      } else if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: unknown }).toDate === 'function') {
        // Firestore Timestamp 객체 (다른 형태)
        serialized[key] = (value as { toDate: () => Date }).toDate().toISOString()
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        // 중첩 객체 재귀 처리
        serialized[key] = serializeData(value as Record<string, unknown>)
      } else {
        serialized[key] = value
      }
    }

    return serialized
  }, [])

  // ========================================================================
  // 2-3. Firestore 컬렉션 데이터 가져오기
  // ========================================================================
  const fetchCollection = useCallback(async (userId: string, collectionName: string): Promise<unknown[]> => {
    try {
      const collectionRef = collection(db, 'users', userId, collectionName)
      const snapshot = await getDocs(collectionRef)

      const documents: unknown[] = []
      snapshot.forEach((docSnap) => {
        const data = docSnap.data()
        documents.push({
          id: docSnap.id,
          ...serializeData(data),
        })
      })

      console.log(`[BackupTab] ${collectionName}: ${documents.length}개 로드`)
      return documents
    } catch (error) {
      console.warn(`[BackupTab] ${collectionName} 로드 실패:`, error)
      return []
    }
  }, [serializeData])

  // ========================================================================
  // 2-4. 백업 생성 핸들러 (실제 Firestore 데이터 수집)
  // ========================================================================
  const handleCreateBackup = useCallback(async () => {
    if (!user) return
    setIsProcessing(true)
    setStatusMessage(null)
    setStatusType(null)

    try {
      console.log('[BackupTab] 백업 생성 시작...')

      // Firestore에서 모든 컬렉션 데이터 수집
      const [textsData, postsData, urlLinksData, scriptsData] = await Promise.all([
        fetchCollection(user.uid, 'texts'),
        fetchCollection(user.uid, 'posts'),      // 트래킹 포스트
        fetchCollection(user.uid, 'urlLinks'),
        fetchCollection(user.uid, 'scripts'),
      ])

      // 백업 데이터 구조 생성 (기존 js/backup.js와 호환)
      const backupData: BackupData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        userId: user.uid,
        userEmail: user.email || 'unknown',
        data: {
          texts: textsData,
          trackingPosts: postsData,
          urlLinks: urlLinksData,
          scripts: scriptsData,
        },
        counts: {
          texts: textsData.length,
          trackingPosts: postsData.length,
          urlLinks: urlLinksData.length,
          scripts: scriptsData.length,
        },
      }

      // 파일명 생성: 500text_backup_YYYYMMDD_HHMMSS.json
      const now = new Date()
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '')
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '')
      const filename = `500text_backup_${dateStr}_${timeStr}.json`

      // JSON 파일 다운로드
      const blob = new Blob([JSON.stringify(backupData, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      // URL 해제
      setTimeout(() => URL.revokeObjectURL(url), 1000)

      setLastBackupDate(new Date())

      const totalCount = textsData.length + postsData.length + urlLinksData.length + scriptsData.length
      setStatusMessage(`백업 완료! (총 ${totalCount}개 항목)`)
      setStatusType('success')
      console.log(`[BackupTab] 백업 생성 완료: ${filename}`)

      // 5초 후 상태 메시지 숨기기
      setTimeout(() => {
        setStatusMessage(null)
        setStatusType(null)
      }, 5000)

    } catch (err) {
      console.error('[BackupTab] 백업 생성 실패:', err)
      setStatusMessage(`백업 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
      setStatusType('error')
    } finally {
      setIsProcessing(false)
    }
  }, [user, fetchCollection])

  // ========================================================================
  // 2-5. 복원 파일 선택 핸들러
  // ========================================================================
  const handleSelectFile = useCallback((file: File) => {
    setRestoreFile(file)
    setStatusMessage(null)
    setStatusType(null)
  }, [])

  // ========================================================================
  // 2-6. ISO 날짜 문자열 여부 확인
  // ========================================================================
  const isISODateString = useCallback((str: string): boolean => {
    const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/
    return isoRegex.test(str)
  }, [])

  // ========================================================================
  // 2-7. JSON 데이터를 Firestore 저장용으로 역직렬화
  // ========================================================================
  // ========================================================================
  // 2-6-1. Firestore Timestamp 객체 ({ seconds, nanoseconds }) 여부 확인
  // ========================================================================
  const isFirestoreTimestamp = useCallback((obj: unknown): obj is { seconds: number; nanoseconds: number } => {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'seconds' in obj &&
      'nanoseconds' in obj &&
      typeof (obj as Record<string, unknown>).seconds === 'number'
    )
  }, [])

  const deserializeData = useCallback((data: Record<string, unknown>): Record<string, unknown> => {
    const deserialized: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined) {
        deserialized[key] = null
      } else if (typeof value === 'string' && isISODateString(value)) {
        // ISO 문자열을 Date로 변환 (Firestore가 자동으로 Timestamp로 변환)
        deserialized[key] = new Date(value)
      } else if (isFirestoreTimestamp(value)) {
        // 레거시 Firestore Timestamp 객체 { seconds, nanoseconds } → Date 변환
        deserialized[key] = new Date(value.seconds * 1000 + value.nanoseconds / 1000000)
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        // 중첩 객체 재귀 처리
        deserialized[key] = deserializeData(value as Record<string, unknown>)
      } else {
        deserialized[key] = value
      }
    }

    return deserialized
  }, [isISODateString, isFirestoreTimestamp])

  // ========================================================================
  // 2-8. 컬렉션에 데이터 복원 (Import)
  // ========================================================================
  const importCollection = useCallback(async (
    userId: string,
    collectionName: string,
    documents: unknown[]
  ): Promise<{ success: number; failed: number }> => {
    const result = { success: 0, failed: 0 }

    if (!documents || documents.length === 0) {
      return result
    }

    for (const docData of documents) {
      try {
        const { id, ...data } = docData as { id?: string; [key: string]: unknown }
        const deserializedData = deserializeData(data)

        if (id) {
          // ID가 있으면 해당 ID로 저장 (덮어쓰기)
          const docRef = doc(db, 'users', userId, collectionName, id)
          await setDoc(docRef, deserializedData, { merge: true })
        } else {
          // ID가 없으면 자동 생성 (addDoc 대신 setDoc with auto ID)
          const newDocRef = doc(collection(db, 'users', userId, collectionName))
          await setDoc(newDocRef, deserializedData)
        }
        result.success++
      } catch (error) {
        console.warn(`[BackupTab] ${collectionName} 문서 저장 실패:`, error)
        result.failed++
      }
    }

    console.log(`[BackupTab] ${collectionName}: 성공 ${result.success}, 실패 ${result.failed}`)
    return result
  }, [deserializeData])

  // ========================================================================
  // 2-9. 복원 실행 핸들러 (실제 Firestore에 데이터 저장)
  // ========================================================================
  const handleConfirmRestore = useCallback(async () => {
    if (!user || !restoreFile) return
    setIsProcessing(true)
    setStatusMessage(null)
    setStatusType(null)

    try {
      console.log('[BackupTab] 복원 실행 시작:', restoreFile.name)

      // 1. 파일 읽기
      const fileContent = await restoreFile.text()
      const rawData = JSON.parse(fileContent)

      // 2. 레거시 포맷 정규화 (data 래퍼 없이 최상위에 texts가 있는 경우)
      const isLegacyFormat = !rawData.data && Array.isArray(rawData.texts)
      const backupData: BackupData = isLegacyFormat
        ? {
            version: 'legacy',
            exportedAt: rawData.exportedAt || '',
            userId: rawData.userId || '',
            userEmail: rawData.userEmail || '',
            data: {
              texts: rawData.texts || [],
              trackingPosts: rawData.trackingPosts || [],
              urlLinks: rawData.urlLinks || [],
              scripts: [],
            },
            counts: {
              texts: rawData.texts?.length || 0,
              trackingPosts: rawData.trackingPosts?.length || 0,
              urlLinks: rawData.urlLinks?.length || 0,
            },
          }
        : (rawData as BackupData)

      // 3. 유효성 검사
      if (!backupData.data || typeof backupData.data !== 'object') {
        throw new Error('유효하지 않은 백업 파일 형식입니다.')
      }

      if (isLegacyFormat) {
        console.log('[BackupTab] 레거시 백업 포맷 감지, 자동 변환 적용')
      }

      // 4. 레거시 텍스트 필드명 정규화 (characterCount → charCount)
      //    Firestore 보안 규칙(isValidTextData)이 charCount 필드를 요구함
      const normalizedTexts = (backupData.data.texts || []).map((text) => {
        const t = text as Record<string, unknown>
        if ('characterCount' in t && !('charCount' in t)) {
          const { characterCount, ...rest } = t
          return { ...rest, charCount: characterCount }
        }
        return text
      })

      // 5. 각 컬렉션에 데이터 복원
      const results = await Promise.all([
        importCollection(user.uid, 'texts', normalizedTexts),
        importCollection(user.uid, 'posts', backupData.data.trackingPosts || []),
        importCollection(user.uid, 'urlLinks', backupData.data.urlLinks || []),
        importCollection(user.uid, 'scripts', backupData.data.scripts || []),
      ])

      const totalSuccess = results.reduce((sum, r) => sum + r.success, 0)
      const totalFailed = results.reduce((sum, r) => sum + r.failed, 0)

      setRestoreFile(null)
      setStatusMessage(`복원 완료! (성공: ${totalSuccess}개, 실패: ${totalFailed}개)`)
      setStatusType(totalFailed > 0 ? 'error' : 'success')

      console.log(`[BackupTab] 복원 완료: 성공 ${totalSuccess}, 실패 ${totalFailed}`)

      // 5초 후 상태 메시지 숨기기
      setTimeout(() => {
        setStatusMessage(null)
        setStatusType(null)
      }, 5000)

    } catch (err) {
      console.error('[BackupTab] 복원 실패:', err)
      setStatusMessage(`복원 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
      setStatusType('error')
      setRestoreFile(null)
    } finally {
      setIsProcessing(false)
    }
  }, [user, restoreFile, importCollection])

  // ========================================================================
  // 2-10. 복원 취소 핸들러
  // ========================================================================
  const handleCancelRestore = useCallback(() => {
    setRestoreFile(null)
  }, [])

  // ========================================================================
  // 2-11. 로그인 필요 렌더링
  // ========================================================================
  if (!user) {
    return (
      <div className={styles.backupTab}>
        <div className={styles.loginRequired}>
          <p>백업 기능을 사용하려면 로그인이 필요합니다.</p>
        </div>
      </div>
    )
  }

  // ========================================================================
  // 2-12. Render
  // ========================================================================
  return (
    <div className={styles.backupTab}>
      {/* ================================================================
          헤더
          ================================================================ */}
      <h3>백업 및 복원</h3>
      <p className={styles.description}>
        데이터를 JSON 파일로 백업하거나 이전 백업에서 복원할 수 있습니다.
      </p>

      {/* ================================================================
          백업/복원 버튼
          ================================================================ */}
      <BackupActions
        onBackup={handleCreateBackup}
        onRestore={handleSelectFile}
        isProcessing={isProcessing}
      />

      {/* ================================================================
          상태 메시지 (성공/에러)
          ================================================================ */}
      {statusMessage && (
        <div
          className={
            statusType === 'success'
              ? styles.successMessage
              : styles.errorMessage
          }
          role="alert"
        >
          <span className={statusType === 'success' ? styles.successIcon : styles.errorIcon}>
            {statusType === 'success' ? '✓' : '✕'}
          </span>
          <p>{statusMessage}</p>
        </div>
      )}

      {/* ================================================================
          백업 히스토리
          ================================================================ */}
      <BackupHistory lastBackupDate={lastBackupDate} />

      {/* ================================================================
          복원 확인 모달
          ================================================================ */}
      {restoreFile && (
        <RestoreConfirm
          file={restoreFile}
          onConfirm={handleConfirmRestore}
          onCancel={handleCancelRestore}
          isProcessing={isProcessing}
        />
      )}
    </div>
  )
}

export default BackupTab
